import { CategoryCodes, getActiveGroups } from '@olxeu-eprivacy-storage/js';
import { getHydraConfig } from '@/config/hydra.ts';
import { getNinjaConfig } from '@/config/ninja.ts';
import { VISIBILITY_STATE } from '@/const/general.ts';
import { Event } from '@/types/events.ts';

export function getHydraDefaultParams(e: Event, lastTrackedPageName?: string) {
  const defaultParams: Record<string, unknown> = {};

  // Session
  defaultParams.sl = e.meta?.sessionParams?.sessionLong;
  defaultParams.s = e.meta?.sessionParams?.session;
  defaultParams.cl = e.meta?.sessionParams?.sessionCountLong;
  defaultParams.c = e.meta?.sessionParams?.sessionCount;

  if (e.meta?.sessionParams?.noCookie) {
    defaultParams.nc = 1;
  }

  // Timestamp - should be generated when the event is added to the dataLayer
  defaultParams.t = e.t ?? Date.now();

  // Add consent. Do not track "strictly necessary" category C0001
  defaultParams.consent = getActiveGroups()
    .filter(group => group !== CategoryCodes.C0001)
    .join(',');

  // Discover some params from the url
  if (e.meta?.invite) {
    defaultParams.iv = e.meta.invite;
  }

  // Host
  if (e.meta?.host) {
    defaultParams.host = e.meta.host;
  }

  // Hash
  if (e.meta?.hash) {
    defaultParams.hash = e.meta.hash;
  }

  // Referrer
  if (e.meta?.referrer) {
    defaultParams.referer = e.meta.referrer;
  }

  const hydraConfig = getHydraConfig();
  // Default invite
  defaultParams.ivd = 'olx-' + hydraConfig.params?.cC.toLowerCase() + '_organic';

  if (hydraConfig.params?.rE !== undefined) {
    defaultParams.rE = hydraConfig.params.rE;
  }

  if (lastTrackedPageName) {
    defaultParams.cP = lastTrackedPageName;
  }

  if (!getNinjaConfig().unitTest) {
    defaultParams.js = 1;
  }

  defaultParams.as = getVisibilityState();

  return defaultParams;
}

/**
 * Adapt apps' logic to web.
 * The value is a number where 0 means that the app is in the foreground and it's active, 1 - the app is in the foreground but not active for any reason, and 2 - the app in the background and it's not visible to the user
 * @see https://console.data.olx.org/user-behaviour/all-products/motors/properties/motors.as/trackers
 */
function getVisibilityState() {
  // visible and active
  if (document.hasFocus()) {
    return VISIBILITY_STATE.ACTIVE;
  }

  // not visible, for whatever reason (on background or covered)
  if (document.hidden) {
    return VISIBILITY_STATE.HIDDEN;
  }

  // (partially) visible, but not on focus
  return VISIBILITY_STATE.INACTIVE_VISIBLE;
}
